import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Plus, Trash2, Pencil, Loader2, Save, X, ShieldCheck, ExternalLink, Power, Megaphone, Settings, Briefcase, Users } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const emptyCat = { name: '', description: '', icon: '' };

// Category icons are stored as lucide component NAMES (e.g. "Megaphone") or an
// emoji. Map known names → components; otherwise render the raw string (emoji).
const CAT_ICONS = { MessageSquare, Megaphone, Settings, Briefcase, Users };

function CatIcon({ name }) {
  const Comp = CAT_ICONS[name];
  if (Comp) return <Comp size={18} className="text-cmic-600" />;
  return <span className="text-lg leading-none">{name || '💬'}</span>;
}

export default function AdminForumPage() {
  const [enabled, setEnabled] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [form, setForm] = useState(null); // {id?, name, description, icon}
  const [saving, setSaving] = useState(false);

  async function load() {
    setLoading(true); setError('');
    try {
      const [s, c] = await Promise.all([
        api.get('/admin/settings'),
        api.get('/forum/categories').catch(() => ({ data: [] })),
      ]);
      setEnabled(s.data?.forum_enabled !== false ? !!s.data?.forum_enabled : false);
      setCats(c.data || []);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Error al cargar');
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function toggleForum() {
    const next = !enabled;
    setSavingToggle(true); setError(''); setOk('');
    try {
      await api.put('/admin/settings', { forum_enabled: next });
      setEnabled(next);
      setOk(next ? 'Foro activado. Ya es visible para los miembros.' : 'Foro desactivado. Se ocultó para los miembros.');
      setTimeout(() => setOk(''), 4000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo cambiar el estado');
    } finally { setSavingToggle(false); }
  }

  async function saveCat() {
    if (!form.name.trim()) { setError('El nombre de la categoría es obligatorio'); return; }
    setSaving(true); setError('');
    try {
      if (form.id) {
        await api.put(`/forum/categories/${form.id}`, { name: form.name, description: form.description, icon: form.icon });
      } else {
        await api.post('/forum/categories', { name: form.name, description: form.description, icon: form.icon });
      }
      setForm(null);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo guardar la categoría');
    } finally { setSaving(false); }
  }

  async function deleteCat(cat) {
    if (!window.confirm(`Eliminar la categoría "${cat.name}"? Se borrarán también sus temas y respuestas. Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete(`/forum/categories/${cat.id}`);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar');
    }
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="admin-forum-page">
      <div className="flex items-center gap-3">
        <MessageSquare className="text-cmic-600" size={24} />
        <h1 className="text-xl sm:text-2xl font-bold text-charcoal-800">Foro</h1>
      </div>

      {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-4 py-3" data-testid="forum-error">{error}</div>}
      {ok && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-lg px-4 py-3" data-testid="forum-ok">{ok}</div>}

      {/* Master toggle */}
      <div className="bg-white rounded-xl border border-gray-200 p-5" data-testid="forum-toggle-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${enabled ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-charcoal-400'}`}>
              <Power size={20} />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-charcoal-800">Estado del foro</h2>
              <p className="text-xs text-charcoal-500 mt-0.5 max-w-md">
                Cuando está <strong>desactivado</strong>, el foro se oculta por completo: para los miembros (menú, tablero, notificaciones y manual) y también aquí en el panel (categorías, moderación y acceso). Actívalo para administrarlo.
              </p>
            </div>
          </div>
          <button
            onClick={toggleForum}
            disabled={savingToggle}
            data-testid="forum-enabled-toggle"
            role="switch"
            aria-checked={enabled}
            className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${enabled ? 'bg-emerald-500' : 'bg-gray-300'} disabled:opacity-60`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
          </button>
        </div>
        <div className={`mt-3 inline-flex items-center gap-2 text-xs font-medium px-2.5 py-1 rounded-full ${enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-charcoal-500'}`}>
          {savingToggle ? <Loader2 size={12} className="animate-spin" /> : null}
          {enabled ? 'Activado — visible para los miembros' : 'Desactivado — oculto para los miembros'}
        </div>
      </div>

      {enabled && (
      <>
      {/* Quick links */}
      <div className="grid sm:grid-cols-2 gap-3">
        <Link to="/foro" data-testid="forum-open-link" className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-4 hover:border-cmic-300 hover:shadow-sm transition-all">
          <ExternalLink size={18} className="text-cmic-600" />
          <div>
            <div className="text-sm font-semibold text-charcoal-800">Abrir el foro</div>
            <div className="text-xs text-charcoal-500">Ver el foro como miembro</div>
          </div>
        </Link>
        <Link to="/foro/moderacion" data-testid="forum-moderation-link" className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-4 hover:border-cmic-300 hover:shadow-sm transition-all">
          <ShieldCheck size={18} className="text-cmic-600" />
          <div>
            <div className="text-sm font-semibold text-charcoal-800">Moderación</div>
            <div className="text-xs text-charcoal-500">Registro de acciones de moderación</div>
          </div>
        </Link>
      </div>

      {/* Categories */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-charcoal-800">Categorías ({cats.length})</h2>
          <button onClick={() => { setForm({ ...emptyCat }); setError(''); }} data-testid="forum-add-category"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-cmic-700 hover:bg-cmic-50 rounded-lg px-3 py-1.5">
            <Plus size={14} /> Nueva categoría
          </button>
        </div>

        {form && (
          <div className="p-5 bg-cmic-50/40 border-b border-gray-100 space-y-3" data-testid="forum-category-form">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3">
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Nombre de la categoría" data-testid="forum-cat-name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
              <input type="text" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })}
                placeholder="Ícono (emoji o nombre: Megaphone, Users…)" data-testid="forum-cat-icon"
                className="w-32 border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Descripción (opcional)" rows={2} data-testid="forum-cat-desc"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" />
            <div className="flex items-center gap-2">
              <button onClick={saveCat} disabled={saving} data-testid="forum-cat-save"
                className="inline-flex items-center gap-2 px-4 py-2 bg-cmic-600 text-white text-sm font-medium rounded-lg hover:bg-cmic-700 disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {form.id ? 'Guardar cambios' : 'Crear categoría'}
              </button>
              <button onClick={() => setForm(null)} className="px-3 py-2 text-sm text-charcoal-500 hover:bg-gray-100 rounded-lg inline-flex items-center gap-1.5">
                <X size={14} /> Cancelar
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-cmic-500" size={22} /></div>
        ) : cats.length === 0 ? (
          <p className="text-sm text-charcoal-500 text-center py-10">Aún no hay categorías. Crea la primera con "Nueva categoría".</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {cats.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-5 py-3" data-testid={`forum-cat-${c.id}`}>
                <span className="w-6 flex items-center justify-center flex-shrink-0"><CatIcon name={c.icon} /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-charcoal-800 truncate">{c.name}</div>
                  <div className="text-xs text-charcoal-500 truncate">{c.description || '—'} · {c.thread_count || 0} temas</div>
                </div>
                <button onClick={() => { setForm({ id: c.id, name: c.name, description: c.description || '', icon: c.icon || '' }); setError(''); }}
                  data-testid={`forum-cat-edit-${c.id}`}
                  className="p-1.5 rounded hover:bg-cmic-50 text-cmic-600"><Pencil size={14} /></button>
                <button onClick={() => deleteCat(c)} data-testid={`forum-cat-delete-${c.id}`}
                  className="p-1.5 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={14} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>
      </>
      )}

      {!enabled && (
        <div className="bg-gray-50 border border-dashed border-gray-200 rounded-xl px-5 py-8 text-center" data-testid="forum-disabled-notice">
          <Power size={22} className="mx-auto mb-2 text-charcoal-300" />
          <p className="text-sm font-medium text-charcoal-600">El foro está desactivado y oculto para todos.</p>
          <p className="text-xs text-charcoal-400 mt-1">Actívalo con el interruptor de arriba para ver y administrar las categorías, la moderación y el acceso al foro.</p>
        </div>
      )}
    </div>
  );
}
